import { useNavigationLinks } from "@circle-react-shared/uikit/HeaderV3/NavigationBar/hooks/useNavigationLinks";
import type { HeaderNavAudienceApiParam } from "@circle-react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { cssAnchorProps } from "@circle-react/helpers/cssAnchorHelpers";
import { useThreeExtraLargeScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useMemo } from "react";

import { MAX_NAV_LINKS, MAX_NAV_LINKS_BEFORE_3XL } from "../constants";
import { MorePopover } from "./MorePopover";
import { NavigationLink } from "./NavigationLink";
import { SkeletonLoader } from "./SkeletonLoader";
import { useResponsiveNavLinks } from "./useResponsiveNavLinks";

interface NavigationBarProps {
  audience?: HeaderNavAudienceApiParam;
  isPreviewMode?: boolean;
}

export const NavigationBar = ({
  audience,
  isPreviewMode = false,
}: NavigationBarProps) => {
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const { links, isLoadingLinks } = useNavigationLinks({
    isDisabled: isPreviewMode,
    audience,
  });
  const is3XlScreen = useThreeExtraLargeScreenMediaQuery();
  const maxVisibleLinks = useMemo(
    () => (is3XlScreen ? MAX_NAV_LINKS : MAX_NAV_LINKS_BEFORE_3XL),
    [is3XlScreen],
  );

  const { visibleLinks, hiddenLinks, containerRef } = useResponsiveNavLinks({
    links,
    maxVisibleLinks,
  });

  if (isLoadingLinks) {
    return <SkeletonLoader count={Math.min(maxVisibleLinks, MAX_NAV_LINKS)} />;
  }

  return (
    <div
      ref={containerRef}
      className="flex w-full min-w-0 justify-center overflow-x-hidden py-1"
    >
      <ul
        className="flex w-max list-none items-center justify-center gap-2"
        data-testid="header-navigation-bar"
        {...cssAnchorProps({ anchor: "app-topbar-nav" })}
      >
        {visibleLinks.map(({ linkTo, label, visibility, ...rest }) => (
          <li key={linkTo} className="flex shrink-0" data-nav-link>
            <NavigationLink
              linkTo={linkTo}
              label={label}
              visibility={visibility}
              renderAsAnchor={Boolean(
                !currentCommunityMember && currentCommunity?.is_private,
              )}
              {...rest}
            />
          </li>
        ))}
        {hiddenLinks.length > 0 && (
          <li className="flex shrink-0">
            <MorePopover items={hiddenLinks} isPreviewMode={isPreviewMode} />
          </li>
        )}
      </ul>
    </div>
  );
};
